import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Plug, Plus, RefreshCw, CheckCircle2, XCircle, AlertCircle, 
  Plane, Activity, Wrench, Cloud, Trash2, Settings2, Zap, Database
} from 'lucide-react';
import { toast } from 'sonner';

const INTEGRATION_TYPES = {
  flight_ops: { label: 'Flight Operations', icon: Plane, color: 'text-sky-500' },
  fdm_foqa: { label: 'FDM/FOQA', icon: Activity, color: 'text-orange-500' },
  maintenance: { label: 'Maintenance', icon: Wrench, color: 'text-violet-500' },
  weather: { label: 'Weather', icon: Cloud, color: 'text-cyan-500' },
  crew: { label: 'Crew Management', icon: Plane, color: 'text-green-500' },
  atc: { label: 'ATC', icon: Zap, color: 'text-yellow-500' }
};

const STATUS_STYLES = {
  active: { label: 'Active', color: 'bg-green-500/20 text-green-400 border-green-500/50' },
  inactive: { label: 'Inactive', color: 'bg-slate-500/20 text-slate-400 border-slate-500/50' },
  error: { label: 'Error', color: 'bg-red-500/20 text-red-400 border-red-500/50' },
  pending: { label: 'Pending', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50' }
};

export default function IntegrationsPage() {
  const { t } = useTranslation();
  const { api } = useAuth();
  const [activeTab, setActiveTab] = useState('integrations');
  const [loading, setLoading] = useState(true);
  const [integrations, setIntegrations] = useState([]);
  const [fdmEvents, setFdmEvents] = useState([]);
  const [fdmStats, setFdmStats] = useState(null);
  const [flights, setFlights] = useState([]);
  const [maintenanceItems, setMaintenanceItems] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [testing, setTesting] = useState(null);
  
  // New integration form
  const [newIntegration, setNewIntegration] = useState({
    name: '',
    integration_type: 'flight_ops',
    api_endpoint: '',
    api_key: '',
    sync_enabled: false,
    sync_interval_minutes: 60
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [intRes, fdmEventsRes, fdmStatsRes, flightsRes, maintRes] = await Promise.all([
        api.get('/integrations'),
        api.get('/integrations/data/fdm-events?limit=20'),
        api.get('/integrations/data/fdm-stats?days=30'),
        api.get('/integrations/data/flights'),
        api.get('/integrations/data/maintenance')
      ]);
      
      setIntegrations(intRes.data.integrations || []);
      setFdmEvents(fdmEventsRes.data.events || []);
      setFdmStats(fdmStatsRes.data);
      setFlights(flightsRes.data.flights || []);
      setMaintenanceItems(maintRes.data.items || []);
    } catch (error) {
      console.error('Failed to fetch integrations data:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAddIntegration = async () => {
    if (!newIntegration.name) {
      toast.error('Please enter a name');
      return;
    }
    
    try {
      await api.post('/integrations', newIntegration);
      toast.success('Integration created');
      setShowAddDialog(false);
      setNewIntegration({
        name: '',
        integration_type: 'flight_ops',
        api_endpoint: '',
        api_key: '',
        sync_enabled: false,
        sync_interval_minutes: 60
      });
      fetchData();
    } catch (error) {
      console.error('Failed to create integration:', error);
      toast.error('Failed to create integration');
    }
  };

  const handleDeleteIntegration = async (id) => {
    if (!confirm('Delete this integration?')) return;
    
    try {
      await api.delete(`/integrations/${id}`);
      toast.success('Integration deleted');
      fetchData();
    } catch (error) {
      console.error('Failed to delete integration:', error);
      toast.error('Failed to delete integration');
    }
  };

  const handleTestConnection = async (id) => {
    setTesting(id);
    try {
      const response = await api.post(`/integrations/${id}/test`);
      if (response.data.success) {
        toast.success(`Connection successful (${response.data.latency_ms}ms)`);
      } else {
        toast.error(response.data.error || 'Connection failed');
      }
    } catch (error) {
      toast.error('Connection test failed');
    } finally {
      setTesting(null);
    }
  };

  const handleToggleSync = async (id, enabled) => {
    try {
      await api.put(`/integrations/${id}`, { sync_enabled: enabled });
      fetchData();
      toast.success(enabled ? 'Sync enabled' : 'Sync disabled');
    } catch (error) {
      toast.error('Failed to update sync settings');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Plug className="w-12 h-12 text-cyan-500 animate-pulse mx-auto mb-4" />
          <p className="text-slate-400">Loading Integrations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="integrations-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white font-barlow tracking-tight flex items-center gap-3">
            <Plug className="w-8 h-8 text-cyan-500" />
            External Integrations
          </h1>
          <p className="text-slate-400 mt-1">Connect with Flight Ops, FDM/FOQA, and Maintenance systems</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchData} variant="outline" className="border-slate-700">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="bg-cyan-600 hover:bg-cyan-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Integration
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-800">
              <DialogHeader>
                <DialogTitle className="text-white">Add New Integration</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label className="text-slate-300">Integration Name</Label>
                  <Input
                    value={newIntegration.name}
                    onChange={(e) => setNewIntegration({...newIntegration, name: e.target.value})}
                    placeholder="e.g., ACME Flight Ops"
                    className="bg-slate-950 border-slate-800 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Integration Type</Label>
                  <Select 
                    value={newIntegration.integration_type}
                    onValueChange={(v) => setNewIntegration({...newIntegration, integration_type: v})}
                  >
                    <SelectTrigger className="bg-slate-950 border-slate-800 mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800">
                      {Object.entries(INTEGRATION_TYPES).map(([key, { label }]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-300">API Endpoint</Label>
                  <Input
                    value={newIntegration.api_endpoint}
                    onChange={(e) => setNewIntegration({...newIntegration, api_endpoint: e.target.value})}
                    placeholder="https://api.example.com/v1"
                    className="bg-slate-950 border-slate-800 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">API Key (Optional)</Label>
                  <Input
                    type="password"
                    value={newIntegration.api_key}
                    onChange={(e) => setNewIntegration({...newIntegration, api_key: e.target.value})}
                    placeholder="••••••••"
                    className="bg-slate-950 border-slate-800 mt-1"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-slate-300">Enable Auto-Sync</Label>
                    <p className="text-slate-500 text-xs">Automatically fetch data at intervals</p>
                  </div>
                  <Switch
                    checked={newIntegration.sync_enabled}
                    onCheckedChange={(v) => setNewIntegration({...newIntegration, sync_enabled: v})}
                  />
                </div>
                {newIntegration.sync_enabled && (
                  <div>
                    <Label className="text-slate-300">Sync Interval (minutes)</Label>
                    <Input
                      type="number"
                      value={newIntegration.sync_interval_minutes}
                      onChange={(e) => setNewIntegration({...newIntegration, sync_interval_minutes: parseInt(e.target.value) || 60})}
                      className="bg-slate-950 border-slate-800 mt-1"
                    />
                  </div>
                )}
                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setShowAddDialog(false)} className="border-slate-700">
                    Cancel
                  </Button>
                  <Button onClick={handleAddIntegration} className="bg-cyan-600 hover:bg-cyan-700">
                    Create Integration
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800/50">
          <TabsTrigger value="integrations" className="data-[state=active]:bg-slate-700">
            <Plug className="w-4 h-4 mr-2" />Integrations
          </TabsTrigger>
          <TabsTrigger value="fdm" className="data-[state=active]:bg-slate-700">
            <Activity className="w-4 h-4 mr-2" />FDM Events
          </TabsTrigger>
          <TabsTrigger value="flights" className="data-[state=active]:bg-slate-700">
            <Plane className="w-4 h-4 mr-2" />Flights
          </TabsTrigger>
          <TabsTrigger value="maintenance" className="data-[state=active]:bg-slate-700">
            <Wrench className="w-4 h-4 mr-2" />Maintenance
          </TabsTrigger>
        </TabsList>

        {/* Integrations Tab */}
        <TabsContent value="integrations" className="space-y-6 mt-6">
          {integrations.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {integrations.map(integration => {
                const typeInfo = INTEGRATION_TYPES[integration.integration_type] || INTEGRATION_TYPES.flight_ops;
                const statusInfo = STATUS_STYLES[integration.status] || STATUS_STYLES.inactive;
                const Icon = typeInfo.icon;
                
                return (
                  <Card key={integration.id} className="bg-slate-900/60 border-slate-800">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center ${typeInfo.color}`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <div>
                            <h3 className="text-white font-medium">{integration.name}</h3>
                            <p className="text-slate-500 text-sm">{typeInfo.label}</p>
                            <Badge variant="outline" className={`mt-2 ${statusInfo.color}`}>
                              {statusInfo.label}
                            </Badge>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleTestConnection(integration.id)}
                            disabled={testing === integration.id}
                            className="text-slate-400 hover:text-white"
                          >
                            {testing === integration.id ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <Zap className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteIntegration(integration.id)}
                            className="text-slate-400 hover:text-red-400"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      
                      <div className="mt-4 pt-4 border-t border-slate-800">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-500">Auto-Sync</span>
                          <Switch
                            checked={integration.sync_enabled}
                            onCheckedChange={(v) => handleToggleSync(integration.id, v)}
                          />
                        </div>
                        {integration.api_endpoint && (
                          <p className="text-slate-600 text-xs mt-2 truncate">{integration.api_endpoint}</p>
                        )}
                        {integration.last_sync && (
                          <p className="text-slate-600 text-xs mt-1">
                            Last sync: {new Date(integration.last_sync).toLocaleString()}
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card className="bg-slate-900/60 border-slate-800">
              <CardContent className="py-12">
                <div className="text-center">
                  <Plug className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">No integrations configured</p>
                  <p className="text-slate-500 text-sm mt-1">Add an integration to connect external systems</p>
                  <Button onClick={() => setShowAddDialog(true)} className="mt-4 bg-cyan-600 hover:bg-cyan-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Integration
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Webhook Info */}
          <Card className="bg-slate-900/60 border-slate-800">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Database className="w-5 h-5 text-cyan-500" />
                Webhook Endpoints
              </CardTitle>
              <CardDescription>External systems can push data to these endpoints</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="bg-sky-600">POST</Badge>
                    <code className="text-cyan-400 text-sm">/api/webhooks/flight-ops</code>
                  </div>
                  <p className="text-slate-500 text-xs mt-1">Receive flight data from Flight Ops systems</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="bg-sky-600">POST</Badge>
                    <code className="text-cyan-400 text-sm">/api/webhooks/fdm</code>
                  </div>
                  <p className="text-slate-500 text-xs mt-1">Receive FDM/FOQA events (auto-creates incidents for high severity)</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="bg-sky-600">POST</Badge>
                    <code className="text-cyan-400 text-sm">/api/webhooks/maintenance</code>
                  </div>
                  <p className="text-slate-500 text-xs mt-1">Receive maintenance items and MEL deferrals</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* FDM Events Tab */}
        <TabsContent value="fdm" className="space-y-6 mt-6">
          {/* FDM Stats */}
          {fdmStats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-slate-900/60 border-slate-800">
                <CardContent className="pt-6">
                  <p className="text-slate-400 text-sm">Total Events (30d)</p>
                  <p className="text-3xl font-bold text-white">{fdmStats.total_events}</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-900/60 border-slate-800">
                <CardContent className="pt-6">
                  <p className="text-slate-400 text-sm">Critical</p>
                  <p className="text-3xl font-bold text-red-500">{fdmStats.by_severity?.critical || 0}</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-900/60 border-slate-800">
                <CardContent className="pt-6">
                  <p className="text-slate-400 text-sm">High</p>
                  <p className="text-3xl font-bold text-orange-500">{fdmStats.by_severity?.high || 0}</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-900/60 border-slate-800">
                <CardContent className="pt-6">
                  <p className="text-slate-400 text-sm">Medium/Low</p>
                  <p className="text-3xl font-bold text-yellow-500">
                    {(fdmStats.by_severity?.medium || 0) + (fdmStats.by_severity?.low || 0)}
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* FDM Events List */}
          <Card className="bg-slate-900/60 border-slate-800">
            <CardHeader>
              <CardTitle className="text-white">Recent FDM Events</CardTitle>
            </CardHeader>
            <CardContent>
              {fdmEvents.length > 0 ? (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {fdmEvents.map((event, i) => (
                      <div key={i} className="flex items-center justify-between bg-slate-800/50 rounded-lg p-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-white font-medium">{event.event_name}</p>
                            <Badge 
                              variant="outline" 
                              className={
                                event.severity === 'critical' ? 'border-red-500 text-red-500' :
                                event.severity === 'high' ? 'border-orange-500 text-orange-500' :
                                'border-yellow-500 text-yellow-500'
                              }
                            >
                              {event.severity}
                            </Badge>
                          </div>
                          <p className="text-slate-500 text-sm">
                            {event.flight_number} • {event.event_category} • {event.parameter}: {event.actual_value} ({event.deviation_percent}% deviation)
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-slate-400 text-sm">{new Date(event.timestamp).toLocaleString()}</p>
                          {event.linked_incident_id && (
                            <Badge variant="secondary" className="mt-1 text-xs">Incident Created</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <div className="text-center py-8">
                  <Activity className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">No FDM events received</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Flights Tab */}
        <TabsContent value="flights" className="space-y-6 mt-6">
          <Card className="bg-slate-900/60 border-slate-800">
            <CardHeader>
              <CardTitle className="text-white">Imported Flights</CardTitle>
              <CardDescription>Flight data received from Flight Ops systems</CardDescription>
            </CardHeader>
            <CardContent>
              {flights.length > 0 ? (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {flights.map((flight, i) => (
                      <div key={i} className="flex items-center justify-between bg-slate-800/50 rounded-lg p-3">
                        <div className="flex items-center gap-4">
                          <Plane className="w-5 h-5 text-sky-500" />
                          <div>
                            <p className="text-white font-medium">{flight.flight_number}</p>
                            <p className="text-slate-500 text-sm">
                              {flight.departure_airport} → {flight.arrival_airport}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-slate-300">{flight.aircraft_registration}</p>
                          <Badge variant="outline" className="mt-1">{flight.status}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <div className="text-center py-8">
                  <Plane className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">No flight data received</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Maintenance Tab */}
        <TabsContent value="maintenance" className="space-y-6 mt-6">
          <Card className="bg-slate-900/60 border-slate-800">
            <CardHeader>
              <CardTitle className="text-white">Maintenance Items</CardTitle>
              <CardDescription>Defects and MEL items from maintenance systems</CardDescription>
            </CardHeader>
            <CardContent>
              {maintenanceItems.length > 0 ? (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {maintenanceItems.map((item, i) => (
                      <div key={i} className="flex items-center justify-between bg-slate-800/50 rounded-lg p-3">
                        <div className="flex items-center gap-4">
                          <Wrench className="w-5 h-5 text-violet-500" />
                          <div>
                            <p className="text-white font-medium">{item.description}</p>
                            <p className="text-slate-500 text-sm">
                              {item.aircraft_registration} • {item.item_type}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge 
                            variant="outline" 
                            className={
                              item.priority === 'aog' ? 'border-red-500 text-red-500' :
                              item.priority === 'high' ? 'border-orange-500 text-orange-500' :
                              'border-slate-500 text-slate-500'
                            }
                          >
                            {item.priority}
                          </Badge>
                          {item.is_deferred && (
                            <Badge variant="secondary" className="ml-2">Deferred</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <div className="text-center py-8">
                  <Wrench className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">No maintenance items received</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
